/**
 * HorseList — Liste des chevaux du propriétaire avec photo, infos et actions
 *
 * App.jsx ne rend ce composant que si le user est propriétaire (signup role)
 * OU s'il possède au moins un cheval. Conséquence : tous les chevaux ici sont
 * possédés par le user (rôle "owner" per-horse), donc pas de check de droits
 * dans la liste — toutes les actions sont disponibles.
 *
 * Archivage (S3) : les chevaux archivés sont masqués par défaut ; un
 * interrupteur les réaffiche (après les actifs, même tri S2).
 *
 * « Ajouter un cheval » (D-NAV-ACTIONS) : bouton d'en-tête + CTA de l'état vide,
 * rendus seulement si `onAddHorse` est fourni — la couche routing le passe sous
 * la même condition que l'ancien onglet « Ajouter » d'AppNav.
 */
import { getTheme, BRAND, BTN } from '../constants/theme';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusCircle } from 'lucide-react';
import ConfirmModal from '../shared/ConfirmModal';
import Toast from '../shared/Toast';
import HorseCard from './HorseCard';
import HorseListToolbar from './HorseListToolbar';
import HorseListEmptyState from './HorseListEmptyState';
import { sortHorses } from './horseListSort';
import { isArchived } from './horseArchiveService';

const HorseList = ({
  horses, darkMode, onViewData, onUploadCSV, onEdit, onDelete, onHealth, onViewDetail, onLive,
  sortMode, onSortChange, sortSaveError, onSetArchived, onAddHorse,
}) => {
  const { t: tr, i18n } = useTranslation(); // i18n aliasé (collision avec le thème `t`)
  const t = getTheme(darkMode);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const clearError = useCallback(() => setErrorMessage(null), []); // réf. stable : Toast la met en deps

  const { active, archived } = useMemo(() => {
    const sorted = sortHorses(horses, sortMode, i18n.language);
    return { active: sorted.filter(h => !isArchived(h)), archived: sorted.filter(isArchived) };
  }, [horses, sortMode, i18n.language]);
  const visible = showArchived ? [...active, ...archived] : active;

  const runArchive = async (horse, archive) => {
    const result = await onSetArchived(horse._id, archive);
    if (result.ok) return;
    if (result.forbidden) setErrorMessage(tr('horses.archive.forbidden'));
    else setErrorMessage(tr(archive ? 'horses.archive.error' : 'horses.archive.unarchiveError'));
  };

  const confirmArchive = () => {
    const horse = archiveTarget;
    setArchiveTarget(null); // fermeture immédiate : anti double-clic pendant l'appel
    runArchive(horse, true);
  };

  const cardHandlers = {
    onViewData, onUploadCSV, onEdit, onDelete, onHealth, onViewDetail, onLive,
    onArchive: setArchiveTarget,
    onUnarchive: (horse) => runArchive(horse, false),
  };

  const archiveMessage = archiveTarget && [
    tr('horses.archive.confirmBody'),
    archiveTarget.equipmentId ? tr('horses.archive.liveWarning') : null,
  ].filter(Boolean).join('\n\n');

  return (
    <div style={{
      background: t.card, padding: '30px', borderRadius: '8px', border: `1px solid ${t.border}`,
      boxShadow: darkMode ? 'none' : t.shadow, marginBottom: '20px',
    }}>
      {/* flexWrap : en largeur smartphone, le bouton passe sous le titre */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '20px' }}>
        <h2 style={{ color: BRAND.blue, margin: 0, fontSize: '20px' }}>
          {tr('horses.list.title')}
        </h2>
        {onAddHorse && (
          <button type="button" onClick={onAddHorse} style={BTN.primary}>
            <PlusCircle size={16} /> {tr('horses.list.add')}
          </button>
        )}
      </div>

      {horses.length === 0 ? (
        <HorseListEmptyState hasArchived={false} onAddHorse={onAddHorse} darkMode={darkMode} />
      ) : (
        <div>
          <HorseListToolbar
            // Trier 0 ou 1 cheval n'a pas de sens : sélecteur masqué
            showSort={visible.length > 1 && Boolean(onSortChange)}
            mode={sortMode} onChange={onSortChange} saveError={sortSaveError}
            archivedCount={archived.length} showArchived={showArchived} onToggleArchived={setShowArchived}
            darkMode={darkMode}
          />
          {active.length === 0 && (
            <HorseListEmptyState hasArchived onAddHorse={onAddHorse} darkMode={darkMode} />
          )}
          {visible.map((horse) => (
            <HorseCard key={horse._id} horse={horse} darkMode={darkMode} {...cardHandlers} />
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={Boolean(archiveTarget)}
        title={archiveTarget ? tr('horses.archive.confirmTitle', { name: archiveTarget.horseName }) : ''}
        message={archiveMessage}
        confirmText={tr('horses.actions.archive')}
        confirmColor={BRAND.blueText}
        onConfirm={confirmArchive}
        onCancel={() => setArchiveTarget(null)}
        darkMode={darkMode}
      />
      <Toast isOpen={Boolean(errorMessage)} type="error" message={errorMessage} onClose={clearError} />
    </div>
  );
};

export default HorseList;
